import { Component, HostListener } from '@angular/core';
import { OnInit } from '@angular/core';
import { ClientService } from '../../services/client.service';
import { Router, ActivatedRoute } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';

interface Employee {
  employee_id: string;
  email_address: string;
  first_name: string;
  last_name: string;
}

interface Job {
  id: number;
  job_code: string;
  client_code: string;
  client_name: string;
  employee_id: number;
  job_title: string;
  description?: string;
  employment_type: string;
  Agreed_Hours: number;
  salary_id?: number;
  posted_date: string;
  closing_date?: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  employee?: Employee;
}

interface Approver {
  id: number;
  first_name: string;
  last_name: string;
  title?: string;
  email: string;
  status: string;
}

interface Client {
  id?: number;
  client_code: string;
  status?: string;
  client_name: string;
  direct_supervisor_name: string;
  website?: string;
  contact_email_primary: string;
  contact_first_name_primary: string;
  contact_last_name_primary: string;
  phone_primary: string;
  contact_email_secondary?: string;
  contact_first_name_secondary?: string;
  contact_last_name_secondary?: string;
  phone_secondary?: string;
  contract_start_date?: string;
  end_of_contract_date?: string;
  email_delivery_method?: string;
  timesheet_link?: string;
  timesheet_last_sent?: string;
  created_at?: string;
  updated_at?: string;
  joblists?: Job[];
  jobPage?: number;
  approvers?: Approver[];
  approversLoading?: boolean;
}

import { CommonModule } from '@angular/common';
import { CardBodyComponent, CardComponent, ColComponent, RowComponent, TableDirective, SpinnerComponent, PaginationModule } from '@coreui/angular';
import { IconModule } from '@coreui/icons-angular';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { DocumentService } from '../../services/document.service';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
  selector: 'app-clients',
  standalone: true,
  imports: [
    CommonModule,
    CardBodyComponent,
    CardComponent,
    ColComponent,
    RowComponent,
    TableDirective,
    SpinnerComponent,
    PaginationModule,
    FormsModule,
    IconModule,
    RouterModule
  ],
  templateUrl: './clients.component.html',
  styleUrls: ['./clients.component.scss'],
  animations: [
    trigger('dropdownAnim', [
      state('closed', style({ opacity: 0, transform: 'translateY(-6px)', pointerEvents: 'none' })),
      state('open', style({ opacity: 1, transform: 'translateY(0)', pointerEvents: 'auto' })),
      transition('closed => open', animate('180ms cubic-bezier(0.4,0.0,0.2,1)')),
      transition('open => closed', animate('140ms cubic-bezier(0.4,0.0,0.2,1)'))
    ])
  ]
})
export class ClientsComponent implements OnInit {
  expandedIndex: number | null = null;
  math = Math;
  clients: Client[] = [];
  loading = false;
  activePage: number = 1;
  totalItems: number = 0;
  itemsPerPage: number = 10;
  totalPages: number = 1;
  filter = {
    search: '',
    statuses: [] as string[],
    email_delivery_method: '',
    sort_by: '',
    sort_direction: ''
  };

  statusDropdownOpen = false;

  readonly clientStatusOptions = ['Great', 'Watch', 'Newly Onboard', 'Disengaged', 'At Risk'];
  showDocSendModal = false;
  docSendTarget: Client | null = null;
  docSendList: any[] = [];
  docSendFiltered: any[] = [];
  docSendSearch = '';
  selectedDocId: number | null = null;
  isSendingDoc = false;

  sendingPortalLinkId: number | null = null;

  // ── Document History modal (client-based) ─────────────────────
  showDocHistoryModal = false;
  docHistoryTarget: Client | null = null;
  clientAssignments: any[] = [];
  isLoadingClientHistory = false;
  copiedToken = '';

  constructor(
    private clientService: ClientService,
    private documentService: DocumentService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  openClientForm() {
    this.router.navigate(['/clients/form']);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['client_code']) {
        this.filter.search = params['client_code'];
        // After loading, expand the first matching row
        this.loadClientsWithExpand(this.activePage, params['client_code']);
      } else {
        this.loadClients(this.activePage);
      }
    });
  }

  loadClientsWithExpand(page: number, clientCode: string) {
    this.loading = true;
    this.clientService.getClients(page, this.itemsPerPage, this.filter).subscribe({
      next: (response: any) => {
        if (Array.isArray(response)) {
          this.clients = response;
          this.totalItems = response.length;
          this.totalPages = 1;
        } else {
          this.clients = response?.data || [];
          this.totalItems = response?.total || 0;
          this.totalPages = Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
        }
        // Find and expand the first matching client
        const idx = this.clients.findIndex(c => c.client_code === clientCode);
        if (idx !== -1) {
          this.expandedIndex = idx;
        } else {
          this.expandedIndex = null;
        }
        this.loading = false;
      },
      error: () => {
        this.clients = [];
        this.loading = false;
      }
    });
  }

  loadClients(page: number) {
    this.loading = true;
    // If pagination is disabled, backend returns an array, otherwise an object with data/total
    this.clientService.getClients(page, this.itemsPerPage, this.filter).subscribe({
      next: (response: any) => {
        if (Array.isArray(response)) {
          this.clients = response;
          this.totalItems = response.length;
          this.totalPages = 1;
        } else {
          this.clients = response?.data || [];
          this.totalItems = response?.total || 0;
          this.totalPages = Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
        }
        this.loading = false;
      },
      error: () => {
        this.clients = [];
        this.loading = false;
      }
    });
  }

  onPageChange(page: number) {
    this.activePage = page;
    this.loadClients(page);
  }

  onItemsPerPageChange() {
    this.activePage = 1;
    this.loadClients(this.activePage);
  }

  applyFilters() {
    this.activePage = 1;
    this.loadClients(this.activePage);
  }

  resetFilters() {
    this.filter = { search: '', statuses: [], email_delivery_method: '', sort_by: '', sort_direction: '' };
    this.applyFilters();
  }

  getHiredCount(client: Client): number {
    return (client.joblists || []).filter(j => j.status === 'Hired').length;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.status-dropdown-wrapper')) {
      this.statusDropdownOpen = false;
    }
  }

  toggleStatusFilter(status: string) {
    const idx = this.filter.statuses.indexOf(status);
    if (idx === -1) {
      this.filter.statuses = [...this.filter.statuses, status];
    } else {
      this.filter.statuses = this.filter.statuses.filter(s => s !== status);
    }
    this.activePage = 1;
    this.loadClients(this.activePage);
  }

  isStatusSelected(status: string): boolean {
    return this.filter.statuses.includes(status);
  }

  clearStatusFilter() {
    this.filter.statuses = [];
    this.activePage = 1;
    this.loadClients(this.activePage);
  }

  getStatusFilterLabel(): string {
    if (this.filter.statuses.length === 0) return 'All Statuses';
    if (this.filter.statuses.length === 1) return this.filter.statuses[0];
    return `${this.filter.statuses.length} statuses selected`;
  }

  sortBy(column: string) {
    if (this.filter.sort_by === column) {
      this.filter.sort_direction = this.filter.sort_direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.filter.sort_by = column;
      this.filter.sort_direction = 'asc';
    }
    this.activePage = 1;
    this.loadClients(this.activePage);
  }

  getChunkedPagination(): (number | 'ellipsis')[] {
    const chunkSize = 5;
    const totalChunks = Math.ceil(this.totalPages / chunkSize);
    const currentChunk = Math.floor((this.activePage - 1) / chunkSize);
    const start = currentChunk * chunkSize + 1;
    const end = Math.min(start + chunkSize - 1, this.totalPages);
    const pages: (number | 'ellipsis')[] = [];
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    if (end < this.totalPages) {
      pages.push('ellipsis');
    }
    if (start > 1) {
      pages.unshift('ellipsis');
    }
    return pages;
  }

  onChunkEllipsisClick(next: boolean) {
    const chunkSize = 5;
    if (next) {
      const nextPage = (Math.floor((this.activePage - 1) / chunkSize) + 1) * chunkSize + 1;
      this.onPageChange(Math.min(nextPage, this.totalPages));
    } else {
      const prevPage = (Math.floor((this.activePage - 1) / chunkSize) - 1) * chunkSize + 1;
      this.onPageChange(Math.max(prevPage, 1));
    }
  }

  editClient(client: Client) {
    this.router.navigate(['/clients/form'], { state: { client } });
  }

  toggleRow(index: number) {
    if (this.expandedIndex === index) {
      this.expandedIndex = null;
      return;
    }
    this.expandedIndex = index;
    const client = this.clients[index];
    if (!client.approvers) {
      client.approversLoading = true;
      this.clientService.getApprovers({ client_code: client.client_code }).subscribe({
        next: (res: any) => {
          client.approvers = res?.data ?? res ?? [];
          client.approversLoading = false;
        },
        error: () => {
          client.approvers = [];
          client.approversLoading = false;
        }
      });
    }
  }

  addJobForClient(client: Client) {
    // Navigates to the job form, passing client_code and client_name as query params
    this.router.navigate(['/jobs/job-form'], { queryParams: { client_code: client.client_code, client_name: client.client_name } });
  }

  // ── Send Document modal methods ───────────────────────────────
  openDocSendModal(client: Client) {
    this.docSendTarget = client;
    this.selectedDocId = null;
    this.docSendSearch = '';
    this.docSendList = [];
    this.docSendFiltered = [];
    this.showDocSendModal = true;
    this.documentService.getAll().subscribe({
      next: (res: any) => {
        const all = res.data ?? res;
        this.docSendList = all.filter((d: any) => (d.type ?? '').toLowerCase() === 'client');
        this.filterDocSendList();
      },
      error: () => {}
    });
  }

  closeDocSendModal() {
    this.showDocSendModal = false;
    this.docSendTarget = null;
  }

  filterDocSendList() {
    const q = this.docSendSearch.trim().toLowerCase();
    this.docSendFiltered = q
      ? this.docSendList.filter((d: any) => d.title.toLowerCase().includes(q))
      : [...this.docSendList];
  }

  confirmDocSend() {
    if (!this.docSendTarget || !this.selectedDocId) return;
    this.isSendingDoc = true;
    this.documentService.send(this.selectedDocId, {
      assignee_type: 'client',
      assignee_ids: [this.docSendTarget.id]
    }).subscribe({
      next: () => {
        this.isSendingDoc = false;
        this.closeDocSendModal();
        this.toastr.success('Document sent successfully!', 'Success');
      },
      error: (err: any) => {
        this.isSendingDoc = false;
        this.toastr.error(err?.error?.message ?? 'Failed to send document.', 'Error');
      }
    });
  }

  // ── Document History modal methods ────────────────────────────
  openDocHistoryModal(client: Client) {
    this.docHistoryTarget = client;
    this.clientAssignments = [];
    this.showDocHistoryModal = true;
    this.isLoadingClientHistory = true;
    this.documentService.getAll().subscribe({
      next: (res: any) => {
        const docs = (res.data ?? res).filter((d: any) => (d.type ?? '').toLowerCase() === 'client');
        if (docs.length === 0) {
          this.isLoadingClientHistory = false;
          return;
        }
        const calls: Observable<any>[] = docs.map((doc: any) =>
          this.documentService.history(doc.id).pipe(catchError(() => of({ data: [] })))
        );
        forkJoin(calls).subscribe((results: any[]) => {
          const found: any[] = [];
          results.forEach((r: any, idx: number) => {
            const list: any[] = r.data ?? r ?? [];
            const match = list.find((a: any) => a.assignee_id === client.id);
            if (match) {
              found.push({ ...match, document_title: docs[idx].title, document_id: docs[idx].id });
            }
          });
          this.clientAssignments = found;
          this.isLoadingClientHistory = false;
        });
      },
      error: () => { this.isLoadingClientHistory = false; }
    });
  }

  closeDocHistoryModal() {
    this.showDocHistoryModal = false;
    this.docHistoryTarget = null;
  }

  copySigningLink(token: string) {
    const url = `${window.location.origin}/sign/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      this.copiedToken = token;
      setTimeout(() => this.copiedToken = '', 2000);
    });
  }

  sendPortalLink(approver: any) {
    this.sendingPortalLinkId = approver.id;
    this.clientService.sendPortalLink(approver.id).subscribe({
      next: () => {
        this.toastr.success(`Portal link sent to ${approver.email}.`, 'Sent');
        this.sendingPortalLinkId = null;
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.message ?? 'Failed to send portal link.', 'Error');
        this.sendingPortalLinkId = null;
      }
    });
  }
}
